import { z } from 'zod'

/**
 * A named grouping of capabilities that a partner can access.
 * Users can turn on or off these capabilities individually.
 *
 * These connection types do not necessarily represent disjoint
 * sets of capabilities. Some may overlap. But they should represent
 * a meaningful grouping of related capabilities, such that it matches
 * why a user would want to enable the connection.
 */
export enum SharingConnectionType {
  ImportApplicationData = 'import_application_data',
  ExportApplicationData = 'export_application_data',
  BidirectionalApplicationData = 'bidirectional_application_data',
  ImportFieldPlanningData = 'import_field_planning_data',
  ExportFieldPlanningData = 'export_field_planning_data',
  BidirectionalFieldPlanningData = 'bidirectional_field_planning_data',
  /**
   * Kind of a special case for Radicle, but the idea is that
   * the grower connects with an agronomist, sending over data
   * such as boundaries and field names, and the agronomist
   * can then send back recommendations for the grower to apply,
   * such as prescriptions and soil sampling data.
   */
  AgronomicAdvisor = 'agronomic_advisor'
}

/**
 * What each connection type lets a partner app do with an operation's data, in
 * a grower's terms.
 */
export const SHARING_CONNECTION_TYPE_MEANINGS: Record<SharingConnectionType, string> = {
  [SharingConnectionType.ImportApplicationData]:
    "The partner app can send as-applied data (planting, application, and harvest files) and the locations and status of the operation's machines into Panorama.",
  [SharingConnectionType.ExportApplicationData]:
    "The partner app can read the operation's as-applied data from Panorama and download its 20|20 files.",
  [SharingConnectionType.BidirectionalApplicationData]:
    "Two-way as-applied data: the partner app can both send as-applied data and machine locations into Panorama and read the operation's as-applied data out of it.",
  [SharingConnectionType.ImportFieldPlanningData]:
    "The partner app can add fields, boundaries, and prescriptions to Panorama, and can see the operation's field list so it knows which field each one belongs to.",
  [SharingConnectionType.ExportFieldPlanningData]:
    "The partner app can read the operation's fields, boundaries, and prescriptions from Panorama.",
  [SharingConnectionType.BidirectionalFieldPlanningData]:
    "Two-way field planning: the partner app can both add fields, boundaries, and prescriptions to Panorama and read the operation's existing ones.",
  [SharingConnectionType.AgronomicAdvisor]:
    "The partner app can read the operation's fields and boundaries, and send fields, boundaries, prescriptions, and soil sample results back into Panorama. It cannot read the operation's prescriptions."
}

/**
 * The connection types a partner route requires, encoded for transport through an
 * API Gateway authorizer context, which carries strings only.
 *
 * Org codes are alphanumeric and connection types contain neither hyphens nor
 * commas, so splitting on the first hyphen and then on commas is unambiguous.
 */
export const partnerAuthCodec = z.codec(
  z.string(),
  z.object({
    orgCode: z.string(),
    connectionTypes: z.array(z.enum(SharingConnectionType))
  }),
  {
    decode: (value) => {
      const separator = value.indexOf('-')
      const orgCode = separator === -1 ? value : value.slice(0, separator)
      const types = separator === -1 ? '' : value.slice(separator + 1)

      return {
        orgCode,
        // Cast is checked immediately: the codec validates this against the
        // output schema, so an unrecognized type rejects the whole header rather
        // than silently narrowing what the caller was granted.
        connectionTypes: types.length === 0 ? [] : (types.split(',') as SharingConnectionType[])
      }
    },
    encode: ({ orgCode, connectionTypes }) => `${orgCode}-${connectionTypes.join(',')}`
  }
)
